import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useEntitlement } from "@/hooks/useEntitlement";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/hooks/use-toast";
import { Loader2, AlertCircle, Clock } from "lucide-react";
import type { SupabaseClient } from "@supabase/supabase-js";

const sb = supabase as unknown as SupabaseClient<any>;

interface MCQFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mcqId: string;
  mcqStem: string;
}

const FEEDBACK_TYPES = [
  { value: "incorrect_answer", label: "Incorrect Answer" },
  { value: "unclear_question", label: "Unclear Question" },
  { value: "typo", label: "Typo or Grammar Error" },
  { value: "outdated_info", label: "Outdated Information" },
  { value: "missing_explanation", label: "Missing/Poor Explanation" },
  { value: "other", label: "Other Issue" },
] as const;

export function MCQFeedbackDialog({
  open,
  onOpenChange,
  mcqId,
  mcqStem,
}: MCQFeedbackDialogProps) {
  const { user } = useAuth();
  const { examSlug } = useEntitlement();
  const [feedbackType, setFeedbackType] = useState<string>("incorrect_answer");
  const [feedbackText, setFeedbackText] = useState("");
  const [suggestedCorrection, setSuggestedCorrection] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    canSubmit: boolean;
    cooldownEnd?: Date;
    dailyCount?: number;
  }>({ canSubmit: true });

  // Check rate limits when dialog opens
  useEffect(() => {
    if (!open || !user) return;

    const checkRateLimits = async () => {
      try {
        // Check daily rate limit (10 per day)
        const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const { count: dailyCount } = await sb
          .from("mcq_user_feedback")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .gte("created_at", last24Hours.toISOString());

        if ((dailyCount || 0) >= 10) {
          setRateLimitInfo({
            canSubmit: false,
            dailyCount: dailyCount || 0,
          });
          return;
        }

        // Check cooldown for this specific MCQ (5 minutes)
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
        const { data: recentFeedback } = await sb
          .from("mcq_user_feedback")
          .select("created_at")
          .eq("user_id", user.id)
          .eq("mcq_id", mcqId)
          .gte("created_at", fiveMinutesAgo.toISOString())
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (recentFeedback) {
          const cooldownEnd = new Date(new Date(recentFeedback.created_at).getTime() + 5 * 60 * 1000);
          setRateLimitInfo({
            canSubmit: false,
            cooldownEnd,
            dailyCount: dailyCount || 0,
          });
          return;
        }

        setRateLimitInfo({
          canSubmit: true,
          dailyCount: dailyCount || 0,
        });
      } catch (error) {
        console.error("Error checking rate limits:", error);
      }
    };

    checkRateLimits();
  }, [open, user, mcqId]);

  const handleSubmit = async () => {
    if (!user) return;

    if (feedbackText.length < 10) {
      toast({
        title: "Feedback too short",
        description: "Please provide at least 10 characters of feedback.",
        variant: "destructive",
      });
      return;
    }

    if (feedbackText.length > 500) {
      toast({
        title: "Feedback too long",
        description: "Please keep feedback under 500 characters.",
        variant: "destructive",
      });
      return;
    }

    if (!rateLimitInfo.canSubmit) {
      toast({
        title: "Rate limit reached",
        description: "Please wait before submitting more feedback.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      // Check for duplicate feedback
      const { data: duplicateCheck } = await sb
        .rpc("check_duplicate_feedback", {
          p_user_id: user.id,
          p_mcq_id: mcqId,
          p_feedback_text: feedbackText.trim(),
        })
        .single();

      if (duplicateCheck && (duplicateCheck as any).is_duplicate) {
        toast({
          title: "Duplicate feedback detected",
          description: "You've already submitted similar feedback for this question recently.",
          variant: "destructive",
        });
        setSubmitting(false);
        return;
      }

      const { error } = await sb.from("mcq_user_feedback").insert({
        mcq_id: mcqId,
        user_id: user.id,
        feedback_type: feedbackType as any,
        feedback_text: feedbackText,
        suggested_correction: suggestedCorrection || null,
        is_paid_user: !!examSlug,
      });

      if (error) throw error;

      toast({
        title: "Feedback submitted!",
        description: "Thank you! We'll review your feedback and get back to you.",
      });

      // Reset form
      setFeedbackText("");
      setSuggestedCorrection("");
      setFeedbackType("incorrect_answer");
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Submission failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Suggest MCQ Improvement</DialogTitle>
          <DialogDescription>
            Help us improve this question. Your feedback will be reviewed by our team.
          </DialogDescription>
        </DialogHeader>

        {/* Rate Limit Warning */}
        {!rateLimitInfo.canSubmit && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {rateLimitInfo.cooldownEnd ? (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>
                    Please wait {Math.ceil((rateLimitInfo.cooldownEnd.getTime() - Date.now()) / 60000)} minutes before submitting feedback for this question again.
                  </span>
                </div>
              ) : (
                <span>
                  You've reached the daily limit of 10 feedback submissions. Please try again tomorrow.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {rateLimitInfo.canSubmit && rateLimitInfo.dailyCount && rateLimitInfo.dailyCount >= 7 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You have {10 - rateLimitInfo.dailyCount} feedback submissions remaining today.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* MCQ Preview */}
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium text-muted-foreground mb-2">Question:</p>
            <p className="text-sm line-clamp-3">{mcqStem}</p>
          </div>

          {/* Feedback Type */}
          <div className="space-y-3">
            <Label>What type of issue did you find?</Label>
            <RadioGroup value={feedbackType} onValueChange={setFeedbackType}>
              {FEEDBACK_TYPES.map((type) => (
                <div key={type.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={type.value} id={type.value} />
                  <Label htmlFor={type.value} className="font-normal cursor-pointer">
                    {type.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Feedback Text */}
          <div className="space-y-2">
            <Label htmlFor="feedback">
              Describe the issue <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="feedback"
              placeholder="Please provide details about the issue you found..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={4}
              maxLength={500}
              disabled={!rateLimitInfo.canSubmit}
            />
            <p className="text-xs text-muted-foreground text-right">
              {feedbackText.length}/500 characters (min 10)
            </p>
          </div>

          {/* Suggested Correction */}
          <div className="space-y-2">
            <Label htmlFor="suggestion">Your suggested correction (optional)</Label>
            <Textarea
              id="suggestion"
              placeholder="If you have a specific suggestion for how to fix this, please share it here..."
              value={suggestedCorrection}
              onChange={(e) => setSuggestedCorrection(e.target.value)}
              rows={3}
              maxLength={200}
              disabled={!rateLimitInfo.canSubmit}
            />
            <p className="text-xs text-muted-foreground text-right">
              {suggestedCorrection.length}/200 characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !rateLimitInfo.canSubmit}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Submit Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
